/** Data access for notification rules, member preferences, webhooks and the event feed. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import { compactParams, type Paged, usePagedQuery } from "@/shared/lib/paging";

export type Rule = Schemas["RuleOut"];
export type RuleIn = Schemas["RuleIn"];
export type Preference = Schemas["PreferenceOut"];
export type Endpoint = Schemas["EndpointOut"];
export type EndpointIn = Schemas["EndpointIn"];
export type EndpointPatch = Schemas["EndpointPatch"];
export type EndpointCreated = Schemas["EndpointCreatedOut"];
export type Delivery = Schemas["DeliveryOut"];
export type CloudEvent = Schemas["EventOut"];

/** Notification channels supported by the backend. */
export const CHANNELS = ["in_app", "email"] as const;
/** Rule audiences. */
export const AUDIENCES = [
  "assignees",
  "team",
  "reporter",
  "asset_owner",
  "role",
  "actor",
  "site_supervisors",
] as const;
/** Domain events that rules and webhooks can subscribe to, grouped by aggregate. */
export const EVENT_CATALOGUE: Record<string, string[]> = {
  workorder: [
    "workorder.created",
    "workorder.generated",
    "workorder.assigned",
    "workorder.updated",
    "workorder.start",
    "workorder.hold",
    "workorder.complete",
    "workorder.verify",
    "workorder.close",
    "workorder.cancel",
  ],
  request: [
    "request.created",
    "request.acknowledge",
    "request.convert",
    "request.resolve",
    "request.reject",
  ],
  asset: [
    "asset.created",
    "asset.updated",
    "asset.status_changed",
    "asset.installed",
    "asset.removed",
    "asset.disposed",
    "asset.position_recorded",
    "asset.geofence_entered",
    "asset.geofence_exited",
  ],
  certificate: [
    "certificate.created",
    "certificate.expiring",
    "certificate.expired",
    "certificate.revoked",
  ],
  plan: [
    "plan.created",
    "plan.cycle_completed",
    "plan.schedule_suspended",
    "plan.reminder",
    "plan.overdue",
  ],
  stock: ["stock.received", "stock.issued", "stock.below_minimum"],
  meter: ["meter.reading_recorded", "meter.reading_voided"],
  document: ["document.version_added", "document.archived", "document.confidential_download"],
  identity: [
    "membership.created",
    "membership.roles_changed",
    "invitation.sent",
    "invitation.accepted",
  ],
};
/** Flat list of all catalogued events. */
export const ALL_EVENTS: string[] = Object.values(EVENT_CATALOGUE).flat();

function invalidate(qc: ReturnType<typeof useQueryClient>, ...keys: string[]): void {
  for (const key of keys) {
    void qc.invalidateQueries({ queryKey: [key] });
  }
}

// ---------------------------------------------------------------- rules
export function useRules(): UseQueryResult<Rule[]> {
  return useQuery({
    queryKey: ["notification-rules"],
    queryFn: async () => unwrap(await api.GET("/api/v1/notifications/rules")),
  });
}

export function useSaveRule(): UseMutationResult<Rule, Error, { id?: string; body: RuleIn }> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      id
        ? unwrap(
            await api.PUT("/api/v1/notifications/rules/{rule_id}", {
              params: { path: { rule_id: id } },
              body,
            }),
          )
        : unwrap(await api.POST("/api/v1/notifications/rules", { body })),
    onSuccess: () => invalidate(qc, "notification-rules"),
  });
}

export function useDeleteRule(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/notifications/rules/{rule_id}", {
          params: { path: { rule_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "notification-rules"),
  });
}

// ---------------------------------------------------------------- preferences
export function usePreferences(): UseQueryResult<Preference[]> {
  return useQuery({
    queryKey: ["notification-preferences"],
    queryFn: async () => unwrap(await api.GET("/api/v1/notifications/preferences")),
  });
}

export function useSetPreference(): UseMutationResult<
  Preference,
  Error,
  { event_type: string; channels: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.PUT("/api/v1/notifications/preferences", { body })),
    onSuccess: () => invalidate(qc, "notification-preferences"),
  });
}

// ---------------------------------------------------------------- webhooks
export function useEndpoints(): UseQueryResult<Endpoint[]> {
  return useQuery({
    queryKey: ["webhooks"],
    queryFn: async () => unwrap(await api.GET("/api/v1/integrations/webhooks")),
  });
}

export function useCreateEndpoint(): UseMutationResult<EndpointCreated, Error, EndpointIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => unwrap(await api.POST("/api/v1/integrations/webhooks", { body })),
    onSuccess: () => invalidate(qc, "webhooks"),
  });
}

export function useUpdateEndpoint(): UseMutationResult<
  Endpoint,
  Error,
  { id: string; body: EndpointPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/integrations/webhooks/{endpoint_id}", {
          params: { path: { endpoint_id: id } },
          body,
        }),
      ),
    onSuccess: () => invalidate(qc, "webhooks"),
  });
}

export function useDeleteEndpoint(): UseMutationResult<unknown, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.DELETE("/api/v1/integrations/webhooks/{endpoint_id}", {
          params: { path: { endpoint_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "webhooks"),
  });
}

export function useRotateSecret(): UseMutationResult<Schemas["SecretOut"], Error, string> {
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/integrations/webhooks/{endpoint_id}/rotate-secret", {
          params: { path: { endpoint_id: id } },
        }),
      ),
  });
}

export function useDeliveries(endpointId: string | null, status?: string): Paged<Delivery> {
  return usePagedQuery(
    ["deliveries", endpointId, status],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/integrations/webhooks/{endpoint_id}/deliveries", {
          params: {
            path: { endpoint_id: endpointId ?? "" },
            query: compactParams({ cursor, status, limit: 50 }),
          },
        }),
      ),
    { enabled: !!endpointId },
  );
}

export function useRedeliver(): UseMutationResult<Delivery, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/integrations/webhooks/deliveries/{delivery_id}/redeliver", {
          params: { path: { delivery_id: id } },
        }),
      ),
    onSuccess: () => invalidate(qc, "deliveries"),
  });
}

export function useEventFeed(filters: { type?: string }): Paged<CloudEvent> {
  return usePagedQuery(["event-feed", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/integrations/events", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}
